import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowDown, ArrowUp, ArrowsClockwise, CaretRight, X as XIcon,
  CheckCircle, Warning, Money as Cash, Receipt,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import type { Caja, CajaSesion, CajaTotalesDia, MovimientoCaja } from '@/lib/typesNeoCore'
import { formatRelative as fmtRelativo } from '@/lib/format'
import { AnularMovimientoModal } from '@/components/AnularMovimientoModal'
import { CerrarCajaModal } from '@/components/CerrarCajaModal'
import { imprimirReciboIngresos, imprimirReciboEgresos } from '@/lib/imprimibles'
import { hoyGT } from '../lib/fechas'

/**
 * Página /neocore/caja — back-office del módulo Caja.
 *
 * Vista del cajero: estado de la sesión activa, totales del día,
 * feed cronológico de movimientos, drawer de detalle con acción de anulación,
 * y modal de arqueo al cerrar.
 *
 * No es donde se inician operaciones del asociado — eso vive en la ficha.
 * Acá solo se ven, anulan y se cierra el día.
 */

export function NeoCoreCajaPage() {
  const { session } = useAuth()
  const [caja, setCaja] = useState<Caja | null>(null)
  const [sesion, setSesion] = useState<CajaSesion | null>(null)
  const [movimientos, setMovimientos] = useState<MovimientoCaja[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMov, setSelectedMov] = useState<MovimientoCaja | null>(null)
  const [anularOpen, setAnularOpen] = useState(false)
  const [cerrarOpen, setCerrarOpen] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)

    // 1. Caja física activa (en NeoCore es la principal)
    const { data: cajaData, error: cajaErr } = await supabase
      .from('caja')
      .select('*')
      .eq('activa', true)
      .eq('tipo', 'FISICA')
      .limit(1)
      .single()

    if (cajaErr) {
      setError(cajaErr.message)
      setLoading(false)
      return
    }
    setCaja(cajaData as Caja)

    // 2. Sesión abierta de esa caja (si existe)
    const { data: sesionData } = await supabase
      .from('caja_sesion')
      .select('*')
      .eq('caja_id', (cajaData as Caja).id)
      .eq('estado', 'abierta')
      .order('abierta_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    setSesion((sesionData as CajaSesion | null) ?? null)

    // 3. Movimientos del día (de esa sesión si existe, sino de hoy)
    const today = hoyGT()
    let query = supabase
      .from('movimiento_caja')
      .select(`
        *,
        tipo_movimiento:tipo_movimiento_caja!inner (codigo, nombre, clase),
        serie:serie_documento_id (codigo, alias, tipo_documento),
        asociado:asociado_id (id, no_asociado, nombre_completo, neo_id),
        pagos:movimiento_caja_pago (
          *,
          boleta:boleta_bancaria_id (
            id, fecha_boleta, numero_boleta, monto_total, monto_aplicado, estado,
            cuenta:cuenta_bancaria_propia_id (banco, numero_cuenta)
          ),
          referencia_bancaria_destino:referencia_bancaria_destino_id (
            id, banco, numero_cuenta, titular_nombre
          )
        )
      `)
      .eq('caja_id', (cajaData as Caja).id)
      .gte('fecha', today)
      .order('fecha', { ascending: false })
      .order('hora', { ascending: false })

    if (sesionData) {
      query = query.eq('caja_sesion_id', (sesionData as CajaSesion).id)
    }

    const { data: movsData, error: movsErr } = await query

    if (movsErr) {
      setError(movsErr.message)
      setLoading(false)
      return
    }

    setMovimientos((movsData ?? []) as unknown as MovimientoCaja[])
    setLoading(false)
  }, [])

  useEffect(() => { reload() }, [reload])

  // Calcular totales del día desde el feed (excluyendo anulados)
  const totales = useMemo<CajaTotalesDia>(() => {
    const apertura = sesion?.monto_apertura ?? 0
    let ingresos = 0, egresos = 0
    let ingCount = 0, egCount = 0
    let efIn = 0, efOut = 0

    for (const m of movimientos) {
      if (m.anulado) continue
      const clase = m.tipo_movimiento?.clase
      if (clase === 'INGRESO') {
        ingresos += Number(m.monto)
        ingCount += 1
      } else if (clase === 'EGRESO') {
        egresos += Number(m.monto)
        egCount += 1
      }
      // Sumar efectivo según pagos
      for (const p of m.pagos ?? []) {
        if (p.tipo === 'efectivo') {
          if (clase === 'INGRESO') efIn += Number(p.monto)
          else if (clase === 'EGRESO') efOut += Number(p.monto)
        }
      }
    }

    return {
      ingresos_total: ingresos,
      ingresos_count: ingCount,
      egresos_total: egresos,
      egresos_count: egCount,
      efectivo_ingreso: efIn,
      efectivo_egreso: efOut,
      efectivo_teorico: apertura + efIn - efOut,
      apertura,
    }
  }, [movimientos, sesion])

  const onAnular = (mov: MovimientoCaja) => {
    setSelectedMov(mov)
    setAnularOpen(true)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 13 }}>
          Cargando…
        </div>
      ) : !caja ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 13 }}>
          No hay caja física activa configurada.
        </div>
      ) : (
        <>
          {/* HEADER de la caja */}
          <CajaHeader
            caja={caja}
            sesion={sesion}
            operadorEmail={session?.user?.email ?? ''}
            onCerrar={() => setCerrarOpen(true)}
          />

          {/* CARDS de totales del día */}
          <TotalesGrid totales={totales} sinSesion={!sesion} />

          {/* FEED cronológico */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, margin: 0, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Movimientos del día
            </h3>
            <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
              {movimientos.length} en total
            </span>
          </div>

          {movimientos.length === 0 ? (
            <EmptyFeed />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--hairline)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--hairline)' }}>
              {movimientos.map((m) => (
                <MovimientoRow key={m.id} mov={m} onClick={() => setSelectedMov(m)} />
              ))}
            </div>
          )}

          {/* Drawer de detalle */}
          {selectedMov && (
            <DetalleDrawer
              mov={selectedMov}
              onClose={() => setSelectedMov(null)}
              onAnular={() => onAnular(selectedMov)}
            />
          )}

          {/* Modales */}
          {selectedMov && (
            <AnularMovimientoModal
              open={anularOpen}
              onClose={() => setAnularOpen(false)}
              movimientoId={selectedMov.id}
              correlativo={selectedMov.correlativo}
              serie={selectedMov.serie?.alias ?? selectedMov.serie?.codigo ?? '—'}
              monto={Number(selectedMov.monto)}
              onSuccess={() => {
                setSelectedMov(null)
                setAnularOpen(false)
                reload()
              }}
            />
          )}

          {sesion && (
            <CerrarCajaModal
              open={cerrarOpen}
              onClose={() => setCerrarOpen(false)}
              sesionId={sesion.id}
              efectivoTeorico={totales.efectivo_teorico}
              apertura={totales.apertura}
              onSuccess={() => {
                setCerrarOpen(false)
                reload()
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

// ============================================================
// CajaHeader — estado de la caja y sesión
// ============================================================

function CajaHeader({
  caja, sesion, operadorEmail, onCerrar,
}: {
  caja: Caja
  sesion: CajaSesion | null
  operadorEmail: string
  onCerrar: () => void
}) {
  const tieneSesion = !!sesion

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
        padding: '18px 22px',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', letterSpacing: -0.4 }}>
              {caja.codigo}
            </span>
            <span style={{ fontSize: 13, color: 'var(--ink-soft)', fontWeight: 500 }}>
              {caja.nombre}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--muted-foreground)' }}>
            {tieneSesion ? (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--ok-ink)', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok-dot)' }} />
                  Sesión abierta
                </span>
                <span>·</span>
                <span>desde {fmtRelativo(sesion!.abierta_at)}</span>
                <span>·</span>
                <span>{operadorEmail}</span>
              </>
            ) : (
              <span style={{ color: 'var(--muted-foreground)' }}>
                Sin sesión activa — se abre automáticamente al primer movimiento
              </span>
            )}
          </div>
        </div>
      </div>

      {tieneSesion && (
        <button
          type="button"
          onClick={onCerrar}
          className="btn btn-ghost"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ArrowsClockwise size={13} weight="bold" />
          Cerrar caja
        </button>
      )}
    </div>
  )
}

// ============================================================
// TotalesGrid — cards de ingresos/egresos/efectivo
// ============================================================

function TotalesGrid({ totales, sinSesion }: { totales: CajaTotalesDia; sinSesion: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
      <TotalCard
        label="Ingresos del día"
        monto={totales.ingresos_total}
        count={totales.ingresos_count}
        Icon={ArrowDown}
        accent="var(--ok-ink)"
        accentBg="var(--ok-bg)"
      />
      <TotalCard
        label="Egresos del día"
        monto={totales.egresos_total}
        count={totales.egresos_count}
        Icon={ArrowUp}
        accent="#dc2626"
        accentBg="#fee2e2"
      />
      <TotalCard
        label="Efectivo en caja"
        monto={totales.efectivo_teorico}
        sub={`apertura Q ${totales.apertura.toFixed(2)} · neto efectivo Q ${(totales.efectivo_ingreso - totales.efectivo_egreso).toFixed(2)}`}
        Icon={Cash}
        accent="var(--brand)"
        accentBg="var(--brand-50)"
        muted={sinSesion}
      />
    </div>
  )
}

function TotalCard({
  label, monto, count, sub, Icon, accent, accentBg, muted,
}: {
  label: string
  monto: number
  count?: number
  sub?: string
  Icon: typeof ArrowDown
  accent: string
  accentBg: string
  muted?: boolean
}) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
        padding: '16px 18px',
        boxShadow: 'var(--shadow-sm)',
        opacity: muted ? 0.65 : 1,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {label}
        </span>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: accentBg, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} weight="bold" />
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)', letterSpacing: -0.6, fontFeatureSettings: '"tnum"', lineHeight: 1.1 }}>
        Q {monto.toFixed(2)}
      </div>
      {count !== undefined && (
        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
          {count} movimiento{count === 1 ? '' : 's'}
        </div>
      )}
      {sub && (
        <div style={{ fontSize: 10.5, color: 'var(--muted-foreground)', lineHeight: 1.4 }}>
          {sub}
        </div>
      )}
    </div>
  )
}

// ============================================================
// MovimientoRow — fila del feed
// ============================================================

function MovimientoRow({ mov, onClick }: { mov: MovimientoCaja; onClick: () => void }) {
  const clase = mov.tipo_movimiento?.clase
  const isIngreso = clase === 'INGRESO'
  const hora = mov.hora?.slice(0, 5) ?? '--:--'

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset', cursor: 'pointer',
        background: 'var(--surface)',
        padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 14,
        transition: 'background 100ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--soft-gray)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface)' }}
    >
      {/* Hora */}
      <div style={{ width: 44, fontSize: 11, color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
        {hora}
      </div>

      {/* Icono dirección */}
      <div
        style={{
          width: 32, height: 32, borderRadius: 8,
          background: mov.anulado ? 'var(--soft-gray)' : (isIngreso ? 'var(--ok-bg)' : '#fee2e2'),
          color: mov.anulado ? 'var(--muted-foreground)' : (isIngreso ? 'var(--ok-ink)' : '#dc2626'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isIngreso ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" />}
      </div>

      {/* Asociado + tipo */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: mov.anulado ? 'var(--muted-foreground)' : 'var(--ink)',
          textDecoration: mov.anulado ? 'line-through' : 'none',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {mov.asociado?.nombre_completo ?? 'Sin asociado'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted-foreground)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {mov.tipo_movimiento?.nombre ?? mov.tipo_movimiento_codigo}
          {mov.anulado && <span style={{ color: '#dc2626', fontWeight: 600 }}> · ANULADO</span>}
        </div>
      </div>

      {/* Monto */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 700,
          color: mov.anulado ? 'var(--muted-foreground)' : (isIngreso ? 'var(--ok-ink)' : '#dc2626'),
          textDecoration: mov.anulado ? 'line-through' : 'none',
          fontFeatureSettings: '"tnum"',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {isIngreso ? '+' : '−'}Q {Number(mov.monto).toFixed(2)}
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
          {mov.serie?.alias ?? '?'}-{String(mov.correlativo).padStart(4, '0')}
        </div>
      </div>

      <CaretRight size={12} color="var(--muted-foreground)" weight="bold" />
    </button>
  )
}

// ============================================================
// EmptyFeed
// ============================================================

function EmptyFeed() {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px dashed var(--hairline)',
        borderRadius: 14,
        padding: '40px 24px',
        textAlign: 'center',
        color: 'var(--muted-foreground)',
      }}
    >
      <Cash size={28} weight="duotone" color="var(--muted-foreground)" style={{ marginBottom: 12 }} />
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>
        No hay movimientos del día todavía.
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 12 }}>
        Las operaciones se inician desde la <Link to="/neocore/asociados" style={{ color: 'var(--brand)' }}>ficha del asociado</Link>.
      </p>
    </div>
  )
}

// ============================================================
// DetalleDrawer — drawer lateral con detalle del movimiento
// ============================================================

function DetalleDrawer({
  mov, onClose, onAnular,
}: {
  mov: MovimientoCaja
  onClose: () => void
  onAnular: () => void
}) {
  const clase = mov.tipo_movimiento?.clase
  const isIngreso = clase === 'INGRESO'

  const onReimprimir = async () => {
    if (!mov.asociado_id) return

    // Fetch en paralelo: DPI del asociado + parámetros de inscripción si aplica
    const [{ data: asocData }, paramsResp, sessionResp] = await Promise.all([
      supabase.from('asociado').select('dpi').eq('id', mov.asociado_id).single(),
      mov.tipo_movimiento_codigo === 'AIO01'
        ? supabase.from('parametro_sistema').select('clave, valor')
            .in('clave', ['aportacion.cuota_ingreso', 'aportacion.monto_inicial'])
        : Promise.resolve({ data: null }),
      supabase.auth.getSession(),
    ])

    const cajero = sessionResp.data.session?.user?.email ?? 'Operador'

    let desglose: Array<{ label: string; monto: number }> | undefined
    if (mov.tipo_movimiento_codigo === 'AIO01' && paramsResp.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ps = (paramsResp.data as Array<{ clave: string; valor: string | null }>)
      const cuota = Number(ps.find((p) => p.clave === 'aportacion.cuota_ingreso')?.valor ?? 50)
      const aporte = Number(ps.find((p) => p.clave === 'aportacion.monto_inicial')?.valor ?? 50)
      desglose = [
        { label: 'Cuota de ingreso', monto: cuota },
        { label: 'Aportación inicial', monto: aporte },
      ]
    }

    const pago = mov.pagos?.[0]
    const boleta = pago?.boleta as
      | { fecha_boleta: string; numero_boleta: string; cuenta?: { banco: string; numero_cuenta: string } | null }
      | null
      | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const refDestino = (pago as any)?.referencia_bancaria_destino as
      | { banco: string; numero_cuenta: string; titular_nombre: string }
      | null
      | undefined

    // Para INGRESO con depósito → mostramos cuenta ORIGEN de la coop (de la boleta)
    // Para EGRESO con transferencia → mostramos cuenta DESTINO del asociado (de la referencia)
    const banco = isIngreso ? boleta?.cuenta?.banco : refDestino?.banco
    const numeroCuenta = isIngreso ? boleta?.cuenta?.numero_cuenta : refDestino?.numero_cuenta

    const reciboData = {
      serie: mov.serie?.codigo ?? '',
      correlativo: mov.correlativo,
      fecha: `${mov.fecha}T${mov.hora}`,
      asociado_no: mov.asociado?.no_asociado ?? '',
      neo_id: mov.asociado?.neo_id,
      asociado_nombre: mov.asociado?.nombre_completo ?? '',
      asociado_dpi: asocData?.dpi ?? '',
      concepto: mov.tipo_movimiento_codigo === 'AIO01'
        ? 'Inscripción asociado'
        : (mov.descripcion ?? mov.tipo_movimiento?.nombre ?? ''),
      desglose,
      monto: Number(mov.monto),
      forma_pago: pago?.tipo ?? 'efectivo',
      cajero,
      banco,
      numero_cuenta: numeroCuenta,
      // boleta solo aplica para ingresos (depósito recibido)
      numero_boleta: isIngreso ? boleta?.numero_boleta : undefined,
      fecha_boleta: isIngreso ? boleta?.fecha_boleta : undefined,
      verification_code: mov.verification_code,
    }

    if (isIngreso) {
      imprimirReciboIngresos(reciboData)
    } else {
      imprimirReciboEgresos(reciboData)
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 18, 25, 0.40)',
          zIndex: 999, animation: 'neoFadeIn 180ms ease',
        }}
      />
      <aside
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(440px, 100%)',
          background: 'var(--surface)',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.10)',
          zIndex: 1000,
          display: 'flex', flexDirection: 'column',
          animation: 'neoSlideIn 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header drawer */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Movimiento {mov.serie?.alias ?? '?'}-{String(mov.correlativo).padStart(4, '0')}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginTop: 2, letterSpacing: -0.3 }}>
              {mov.tipo_movimiento?.nombre ?? mov.tipo_movimiento_codigo}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" style={{ all: 'unset', cursor: 'pointer', padding: 6, color: 'var(--muted-foreground)' }}>
            <XIcon size={16} weight="bold" />
          </button>
        </div>

        {/* Body scrollable */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Banner anulado */}
          {mov.anulado && (
            <div style={{
              padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, fontSize: 12, color: '#991b1b', lineHeight: 1.55,
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <Warning size={14} weight="bold" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong style={{ fontWeight: 700, display: 'block' }}>Movimiento anulado</strong>
                {mov.motivo_anulacion}
                <div style={{ fontSize: 10.5, color: '#7f1d1d', marginTop: 4 }}>
                  {mov.anulado_at && fmtRelativo(mov.anulado_at)}
                </div>
              </div>
            </div>
          )}

          {/* Monto destacado */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Monto
            </div>
            <div style={{
              fontSize: 32, fontWeight: 800,
              color: mov.anulado ? 'var(--muted-foreground)' : (isIngreso ? 'var(--ok-ink)' : '#dc2626'),
              marginTop: 4, letterSpacing: -1, fontFeatureSettings: '"tnum"',
              textDecoration: mov.anulado ? 'line-through' : 'none',
            }}>
              {isIngreso ? '+' : '−'}Q {Number(mov.monto).toFixed(2)}
            </div>
          </div>

          {/* Asociado */}
          {mov.asociado && (
            <Link
              to={`/neocore/asociados/${mov.asociado.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px',
                background: 'var(--soft-gray)', border: '1px solid var(--hairline)',
                borderRadius: 10, textDecoration: 'none',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Asociado
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {mov.asociado.nombre_completo}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                  {formatNeoIdShort(mov.asociado.neo_id)} · {mov.asociado.no_asociado}
                </div>
              </div>
              <CaretRight size={14} color="var(--muted-foreground)" weight="bold" />
            </Link>
          )}

          {/* Detalles */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <KvDetail label="Fecha" value={`${formatFechaCorta(mov.fecha)} · ${mov.hora?.slice(0, 5) ?? ''}`} />
            <KvDetail label="Tipo" value={mov.tipo_movimiento?.codigo ?? mov.tipo_movimiento_codigo} mono />
            <KvDetail label="Serie" value={`${mov.serie?.codigo ?? '—'}`} mono small />
            <KvDetail label="Correlativo" value={String(mov.correlativo).padStart(4, '0')} mono />
            {mov.descripcion && <KvDetail label="Descripción" value={mov.descripcion} />}
          </div>

          {/* Formas de pago */}
          {mov.pagos && mov.pagos.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
                Formas de pago
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {mov.pagos.map((p) => (
                  <div key={p.id} style={{
                    padding: '10px 12px',
                    background: 'var(--surface)', border: '1px solid var(--hairline)',
                    borderRadius: 10, fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                        {prettyFormaPago(p.tipo)}
                      </span>
                      <span style={{ fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
                        Q {Number(p.monto).toFixed(2)}
                      </span>
                    </div>
                    {p.boleta && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--hairline)', fontSize: 11, color: 'var(--ink-soft)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Boleta {p.boleta.numero_boleta}</span>
                        <span style={{ color: p.boleta.estado === 'conciliada' ? 'var(--ok-ink)' : 'var(--muted-foreground)', fontWeight: 600 }}>
                          {p.boleta.estado}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer del drawer con acciones */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          {/* Reimprimir siempre disponible — incluso para anulados (queda como copia con marca de anulación) */}
          {clase === 'INGRESO' && (
            <button
              type="button"
              onClick={onReimprimir}
              className="btn btn-ghost"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              disabled={!mov.asociado_id}
            >
              <Receipt size={13} weight="bold" />
              Reimprimir comprobante
            </button>
          )}

          {!mov.anulado ? (
            <button
              type="button"
              onClick={onAnular}
              className="btn btn-ghost"
              style={{ color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
            >
              <Warning size={13} weight="bold" />
              Anular movimiento
            </button>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--muted-foreground)', fontSize: 11, marginLeft: 'auto' }}>
              <CheckCircle size={14} weight="duotone" color="var(--muted-foreground)" />
              Anulación registrada en histórico
            </span>
          )}
        </div>
      </aside>

      <style>{`
        @keyframes neoFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes neoSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}

function KvDetail({ label, value, mono, small }: { label: string; value: string; mono?: boolean; small?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', fontSize: 12,
      borderBottom: '1px solid var(--hairline)',
    }}>
      <span style={{ color: 'var(--muted-foreground)' }}>{label}</span>
      <span style={{
        color: 'var(--ink)', fontWeight: 600,
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        fontSize: small ? 11 : 12,
        textAlign: 'right', maxWidth: '60%',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {value}
      </span>
    </div>
  )
}

// ============================================================
// Helpers
// ============================================================

function formatNeoIdShort(neoId: string): string {
  const digits = neoId.replace(/\D/g, '').padStart(6, '0').slice(-6)
  return `NEO ${digits.slice(0, 3)} ${digits.slice(3, 6)}`
}

function formatFechaCorta(iso: string): string {
  // DATE 'YYYY-MM-DD': anclar mediodía para no correrse un día en GT (UTC-6)
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso)
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

function prettyFormaPago(t: string): string {
  const map: Record<string, string> = {
    efectivo: 'Efectivo',
    deposito: 'Depósito bancario',
    cheque: 'Cheque',
    transferencia_tercero: 'Transferencia de tercero',
    transferencia_bancaria: 'Transferencia bancaria',
  }
  return map[t] ?? t
}

